import { Col, Menu, Row, Skeleton } from "antd";
import type { MenuProps } from "antd";
import { Menu as MenuIcon, Database, AlertCircle, Globe, Activity, GitBranch, TrendingUp, Settings, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { isNewUI } from "../../v2/flag";
import { EntitySettingsTabs } from "../../v2/components/EntitySettingsTabs";
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router";
import { CONFIG_CATEGORIES } from "../../enums/enums";
import MenuItemNotification from "../../components/MenuItemNotification";
import { getEnvironmentService } from "../../backend";
import { GetResourceResponse } from "@microtica/ms-engine-sdk";
import { ComponentDetails, ComponentDomain, IComponentDetails, IEnvDetails } from "../../types";
import { isResourceDeployed } from "../../components/DeploymentStatusTag";

export default function ResourceSettingsRoot() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const { projectId, envId, componentId, clusterId } = useParams() as {
        projectId: string;
        envId: string;
        componentId: string;
        clusterId: string;
    };
    const { env } = useOutletContext<{ env: IEnvDetails }>();
    const [component, setComponent] = useState<IComponentDetails>();
    const [menuItems, setMenuItems] = useState<MenuProps["items"]>();
    const [activeTab, setActiveTab] = useState("general");

    useEffect(() => {
        const loadData = async () => {
            const [{ data: resource }, { data: { outputs } }] = await Promise.all([
                getEnvironmentService().getResource(envId, componentId, projectId),
                getEnvironmentService()
                    .getOutputsForResource(envId, componentId, projectId)
                    .catch(_ => ({ data: { outputs: [] } }))
            ]);

            const component = env.resources.find(c => c.name === componentId);

            if (!component) return;

            const domains = outputs.reduce((acc, curr) => {
                if (curr.key.endsWith("AccessUrl")) {
                    const prefix = curr.key.split("AccessUrl")[0] || "";
                    const CNAME = outputs.find(o => o.key === `${prefix}CNAME`)?.value;
                    const accessUrl = curr.value;
                    if (accessUrl && CNAME) {
                        acc.push({
                            // if accessUrl equals the loadbalancer's DNS name (CNAME), display "Assign Custom Domain" btn 
                            // We must lowercase the CNAME since the URL utility is automatically lowecasing the hostname
                            displayAssignDomainBtn: !!accessUrl && !!CNAME && new URL(accessUrl).hostname === CNAME.toLowerCase(),
                            eligibleForDomainConfiguration: !!accessUrl && !!CNAME,
                            accessUrl,
                            CNAME,
                            domainProp: curr.key,
                            propPrefix: prefix
                        });
                    }
                }
                return acc;
            }, [] as ComponentDomain[]);

            const accessUrl = outputs?.find(o => o.key === "AccessUrl")?.value;
            const CNAME = outputs?.find(o => o.key === "CNAME")?.value;
            const componentDetails = {
                ...component?.component,
                ...(resource as GetResourceResponse & { component: ComponentDetails }).component
            };
            const componentProps = componentDetails.schema.properties.inputs.properties!;

            const items: MenuProps["items"] = [];

            if (Object.values(
                componentProps
            ).some(v => !CONFIG_CATEGORIES.includes(v.category!))
            ) {
                items.push({
                    key: "general",
                    label: <MenuItemNotification
                        icon={<MenuIcon size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="General"
                    />
                });
            }

            if (Object.values(
                componentProps
            ).find(v => ["scaling", "autoscaling", "allocation"].includes(v.category!))
            ) {
                items.push({
                    key: "scaling",
                    label: <MenuItemNotification
                        icon={<TrendingUp size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Scaling"
                    />
                });
            }

            // The new UI has no monitoring page
            if (clusterId && isResourceDeployed(resource.status!) && !isNewUI()) {
                items.push({
                    key: "monitoring",
                    label: <MenuItemNotification
                        icon={<Activity size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Monitoring"
                        badgeText="New"
                    />
                });
            }

            if (Object.values(
                componentProps
            ).find(v => ["alarm"].includes(v.category!))
            ) {
                items.push({
                    key: "alarms",
                    label: <MenuItemNotification
                        icon={<AlertTriangle size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Alarms"
                        badgeText="New"
                    />
                });
            }

            if (Object.values(
                componentProps
            ).find(v => ["storage"].includes(v.category!))
            ) {
                items.push({
                    key: "storage",
                    label: <MenuItemNotification
                        icon={<Database size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Storage"
                    />
                });
            }

            if (!!accessUrl && !!CNAME) {
                items.push({
                    key: "domain",
                    label: <MenuItemNotification
                        icon={<Globe size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Domain"
                    />
                })
            }

            if (!componentDetails.isPublic) {
                items.push({
                    key: "pipeline",
                    label: <MenuItemNotification
                        icon={<GitBranch size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Pipeline"
                    />
                })
            }

            if (Object.values(
                componentProps
            ).find(v => ["variables"].includes(v.category!))
            ) {
                items.push({
                    key: "variables",
                    label: <MenuItemNotification
                        icon={<MenuIcon size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Variables"
                    />
                });
            }

            if (Object.values(
                componentProps
            ).find(v => ["advanced"].includes(v.category!))
            ) {
                items.push({
                    key: "advanced",
                    label: <MenuItemNotification
                        icon={<Settings size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                        text="Advanced"
                    />
                });
            }

            items.push({
                key: "danger-zone",
                label: <MenuItemNotification
                    icon={<AlertCircle size={16} style={{ display: 'flex', alignItems: 'center' }} />}
                    text="Danger Zone"
                />,
                danger: true
            });

            setMenuItems(items);
            setComponent({
                name: component.name,
                version: componentDetails.version,
                status: component.status,
                component: {
                    id: componentDetails.id,
                    name: componentDetails.name,
                    description: componentDetails.description,
                    schema: componentDetails.schema,
                    pipelineId: componentDetails.pipelineId,
                    isPublic: componentDetails.isPublic
                },
                domain: {
                    // if accessUrl equals the loadbalancer's DNS name (CNAME), display "Assign Custom Domain" btn 
                    // We must lowercase the CNAME since the URL utility is automatically lowecasing the hostname
                    displayAssignDomainBtn: !!accessUrl && !!CNAME && new URL(accessUrl).hostname === CNAME.toLowerCase(),
                    eligibleForDomainConfiguration: !!accessUrl && !!CNAME,
                    accessUrl,
                    CNAME
                },
                domains,
                configurations: component.configurations
            });
            setLoading(false);
        }

        if (env) {
            loadData();
        }
    }, [env]);

    useEffect(() => {
        if (menuItems) {
            const tabIndex = clusterId ? 9 : 8;

            if (!location.pathname.split("/")[tabIndex]) {
                navigate(`${location.pathname}/${menuItems[0]?.key}`, { replace: true });
                setActiveTab(menuItems[0]?.key as string);
            } else {
                setActiveTab(location.pathname.split("/")[tabIndex]);
            }
        }
    }, [menuItems]);

    // The new UI shows the tabs as pills above the tab, like project settings
    if (isNewUI()) {
        return loading ? <Skeleton /> : (
            <EntitySettingsTabs items={menuItems} active={activeTab} onSelect={key => { navigate(key, { replace: true }); setActiveTab(key); }}>
                <Outlet context={{ env, component }} />
            </EntitySettingsTabs>
        );
    }

    return (
        <Row gutter={24}>
            <Col span={6}>
                {
                    loading ? <Skeleton /> :
                        <Menu
                            mode="inline"
                            className="ant-card-bordered"
                            selectedKeys={[activeTab]}
                            onSelect={(item) => {
                                navigate(item.key, { replace: true });
                                setActiveTab(item.key);
                            }}
                            items={menuItems}
                        />
                }
            </Col>
            <Col span={18}>
                {
                    loading ? <Skeleton /> :
                        <Outlet context={{ env, component }} />
                }
            </Col>
        </Row>
    )
}